// Updated ShowOnStartupButton.tsx

import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../Settings/SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import React, { useMemo } from 'react';
import { isShapeShownOnStartUp } from './isShapeShownOnStartUp';
import { PiStar, PiStarFill } from 'react-icons/pi';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  addShapeToFavoritesWithOrdering,
  removeShapeFromFavoritesWithOrdering,
} from '../Map/LayersOnStart/layerOrdering';

interface ShowOnStartupButtonProps {
  shape: GeoJson;
}

export const ShowOnStartupButton = ({ shape }: ShowOnStartupButtonProps) => {
  const { messages } = useLocalization();
  const { settings, dataUpdatedAt } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();
  const shapeId = shape?.properties?.id ? shape.properties.id : 'tempShapeId';

  const isShownOnStartup = useMemo(() => {
    return isShapeShownOnStartUp(settings, shapeId);
  }, [settings, dataUpdatedAt, shapeId]);

  const onShowOnStartup = () => {
    // Use the helper function that updates both favorites and layer ordering
    const settingsToSave = addShapeToFavoritesWithOrdering(settings, shapeId);

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-start-up-shapes-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsFavoriteShapesSuccessUpdateMessage,
        });
      },
      onError: () => {
        toast({
          className: 'update-start-up-shapes-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsFavoriteShapesFailedUpdateMessage,
          variant: 'destructive',
        });
      },
    });
  };

  const onRemoveOnStartup = () => {
    // Use the helper function that removes from both favorites and layer ordering
    const settingsToSave = removeShapeFromFavoritesWithOrdering(
      settings,
      shapeId
    );

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-start-up-shapes-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsFavoriteShapesSuccessHideMessage,
        });
      },
      onError: () => {
        toast({
          className: 'update-start-up-shapes-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsFavoriteShapesFailedHideMessage,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="smIcon"
          variant="ghost"
          onClick={isShownOnStartup ? onRemoveOnStartup : onShowOnStartup}
        >
          {isShownOnStartup ? (
            <PiStarFill className="h-4 w-4 text-yellow-400" />
          ) : (
            <PiStar className="h-4 w-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isShownOnStartup
          ? 'No longer show shape on map on startup'
          : 'Add shape to map on startup'}
      </TooltipContent>
    </Tooltip>
  );
};
